import { Button, Divider, FormControl, Grid, MenuItem, styled, Typography } from "@mui/material";
import { Box } from "@mui/material";
import { AssetRetrieval } from "hooks/assetRetrievals/useAssetRetrieval";
import { GetFieldPropsType } from "./SelectBox";
import InsuranceComponent from "./InsuranceComponent";
import { Label } from "./SelectBox";
import { AppSelect, InputBase, serialNumberProps } from "components/common/Input";
import { DeviceType } from "types/assetRetrieval";
import { LibraryAddOutlined, RemoveCircleOutlineOutlined } from "@mui/icons-material";
import { Fragment } from "react";
import { FieldArray, FieldArrayRenderProps } from "formik";

const CustomButton = styled(Button)(() => ({
  textTransform: "none",
  "& .MuiButton-startIcon": {
    marginRight: "2px",
  },
}));

const MAX_ITEMS = 10;

// Helper to generate unique ID for each device
const generateTempId = () => `temp-${Date.now()}-${Math.random()}`;

const BulkBoxInputs = ({
  getFieldProps,
  touched,
  errors,
  values,
}: {
  getFieldProps: GetFieldPropsType;
  touched: Record<string, boolean>;
  errors: Record<string, string>;
  values: AssetRetrieval;
}) => {
  const ExtraBulkBoxSection = ({ index, helpers }: { index: number; helpers: FieldArrayRenderProps }) => (
    <Grid container spacing={2} mt={2}>
      <Grid item xs={8} lg={4} pl={1.5}>
        <CustomButton
          onMouseDown={(e) => {
            e.preventDefault();
            helpers.push({ deviceType: DeviceType.laptop, _tempId: generateTempId() });
          }}
          disabled={(values.devices?.length || 0) >= MAX_ITEMS}
          variant="text"
          color="primary"
          startIcon={<LibraryAddOutlined />}
          sx={{ textTransform: "none" }}
        >
          Add Item
        </CustomButton>
        <CustomButton
          onMouseDown={(e) => {
            e.preventDefault();
            helpers.remove(index);
          }}
          disabled={(values.devices?.length || 0) <= 1}
          variant="text"
          color="neutral"
          startIcon={<RemoveCircleOutlineOutlined />}
          sx={{ textTransform: "none" }}
        >
          Remove
        </CustomButton>
      </Grid>
      <Grid item xs={4} lg={8} mt={1}>
        <Typography variant="body1" textAlign="right" color="text.secondary" fontSize="14px">
          ({index + 1}/{MAX_ITEMS})
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Box mt={2} sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2 }}>
        <InsuranceComponent fullWidth={true} getFieldProps={getFieldProps} />
      </Box>
      <FieldArray name="devices">
        {(helpers) => (
          <>
            {values.devices?.map((device, index) => (
              <Fragment key={(device as any)._tempId || `index-${index}`}>
                <Box mt={2} sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2 }}>
                  <FormControl variant="standard">
                    <Label>Manufacturer</Label>
                    <InputBase
                      type="text"
                      {...getFieldProps<string>(`devices.${index}.manufacturer`)}
                      error={touched.manufacturer && !!errors.manufacturer}
                      helperText={touched.manufacturer && errors.manufacturer}
                    />
                  </FormControl>
                  <FormControl variant="standard">
                    <Label>Device Type</Label>
                    <AppSelect
                      {...getFieldProps<string>(`devices.${index}.deviceType`)}
                      value={values.devices?.[index]?.deviceType || DeviceType.laptop}
                    >
                      <MenuItem value={DeviceType.laptop}>Laptop</MenuItem>
                      <MenuItem value={DeviceType.tablet}>Tablet</MenuItem>
                    </AppSelect>
                  </FormControl>
                  <FormControl variant="standard">
                    <Label>Serial No</Label>
                    <InputBase
                      {...serialNumberProps(getFieldProps<string>(`devices.${index}.serialNumber`))}
                      error={touched.serialNumber && !!errors.serialNumber}
                      helperText={touched.serialNumber && errors.serialNumber}
                    />
                  </FormControl>
                  <FormControl variant="standard">
                    <Label>Model</Label>
                    <InputBase
                      type="text"
                      {...getFieldProps<string>(`devices.${index}.model`)}
                      error={touched.model && !!errors.model}
                      helperText={touched.model && errors.model}
                    />
                  </FormControl>
                </Box>
                <ExtraBulkBoxSection index={index} helpers={helpers} />
              </Fragment>
            ))}
          </>
        )}
      </FieldArray>
    </>
  );
};

export default BulkBoxInputs;
